import { create } from 'zustand'

interface NavigationHistoryStore {
  /**
   * このセッション（フルロード以降）でアプリ内遷移した履歴エントリの Next.js 履歴キー列。
   * 先頭がフルロードしたエントリ。メモリ上のみで保持し、リロードで消える（#3665）。
   *
   * Next.js Pages Router は履歴エントリの history.state に { url, as, options, __N, key } を書き、
   * key は pushState のときだけ新規発行、replaceState（shallow 含む）は直前の key を引き継ぎ、
   * popstate では復元したエントリの key に戻る。この性質で push / replace / back / forward を区別する。
   */
  keys: string[]
  /** keys の中で現在表示しているエントリの位置。keys が空のときは -1 */
  index: number
  /**
   * 先頭エントリ（フルロードしたエントリ）の 1 つ前が、同じアプリ内のページか。
   * アプリ内からフルロードで遷移してきた場合（メニューの「リセット」の location.href、
   * デプロイ直後に Next.js がハードナビゲーションへフォールバックした場合など）に true になる。
   */
  rootHasInAppPrevious: boolean
  /**
   * routeChangeComplete 時に現在の履歴キーを記録する。
   * @param viaPopState ブラウザの戻る / 進む（popstate）でこのエントリに来たか
   */
  record: (key: string, viaPopState: boolean) => void
  /**
   * フルロード時の初期化。現在のエントリだけを持つ状態から始める。
   * @param rootHasInAppPrevious このエントリの 1 つ前が同じアプリ内のページか
   */
  reset: (initialKey: string | null, rootHasInAppPrevious: boolean) => void
}

export const useNavigationHistoryStore = create<NavigationHistoryStore>((set) => ({
  keys: [],
  index: -1,
  rootHasInAppPrevious: false,
  record: (key, viaPopState) =>
    set((state) => {
      const known = state.keys.indexOf(key)
      if (known >= 0) {
        // 現在位置と同じ = replace / shallow。別の位置 = 戻る / 進む。
        // 進むで戻って来られるよう、戻っても先のエントリは捨てない
        return known === state.index ? state : { index: known }
      }
      if (viaPopState) {
        // 戻る / 進むで未知のエントリに来た = リロード前など、このセッションが把握していない履歴。
        // 前後関係が分からないため「戻り先なし」に倒す（アプリ外へ出るより HOME へ送る方が安全）
        return { keys: [key], index: 0, rootHasInAppPrevious: false }
      }
      // push。現在位置より先（進む用に残していたエントリ）は上書きされる
      const keys = [...state.keys.slice(0, state.index + 1), key]
      return { keys, index: keys.length - 1 }
    }),
  reset: (initialKey, rootHasInAppPrevious) =>
    set(
      initialKey !== null
        ? { keys: [initialKey], index: 0, rootHasInAppPrevious }
        : // 履歴キーが取れないときは位置を追跡できないため、戻り先なしに倒す
          { keys: [], index: -1, rootHasInAppPrevious: false },
    ),
}))

/** アプリ内に戻り先の履歴エントリがあるか */
export const selectCanGoBack = (state: NavigationHistoryStore): boolean =>
  state.index > 0 || (state.index === 0 && state.rootHasInAppPrevious)
